import type { BasketItem, Product, Variant } from '@scayle/storefront-nuxt'
import { useCurrentShop } from '#storefront/composables'
import {
  mapProductToEcommerceItem,
  mapAdditionalInfo,
  calculateEcommerceValue,
} from '~/utils'
import { useProductClickStorage } from '~/composables/useProductClickStorage'
import type {
  TrackingEvent,
  TrackingPayload,
  ProductListData,
  EcommerceItem,
} from '~/types/tracking'

const collectBasketItems = (
  items: BasketItem<Product, Variant>[] = [],
  options: { listName: string; listId: string },
) => {
  return items.map(
    (basketItem): ProductListData => ({
      product: basketItem.product,
      list: {
        name: options.listName,
        id: options.listId,
      },
      quantity: basketItem.quantity,
      variant: basketItem.variant,
    }),
  )
}

const useCheckoutEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => void,
) => {
  const currencyCode = useCurrentShop().value!.currency
  const { getProductClick } = useProductClickStorage()

  return {
    trackBeginCheckout: (
      items: BasketItem<Product, Variant>[] = [],
      listName: string,
      listId: string,
      originLocation: string = 'cart_page',
    ) => {
      // Convert basket items to ProductListData first
      const productListItems = collectBasketItems(items, { listName, listId })

      // Convert ProductListData to EcommerceItem and apply localStorage data
      const ecommerceItems: EcommerceItem[] = productListItems.map(
        (payload) => {
          const additionalInfo = mapAdditionalInfo(payload, undefined)

          // Get stored product click data from localStorage
          const storedProductClick = getProductClick(payload.product.id)

          // Use stored wishlist data if available, otherwise use default list info
          const finalListInfo = storedProductClick
            ? {
                item_list_id: storedProductClick.item_list_id,
                item_list_name: storedProductClick.item_list_name,
              }
            : {
                item_list_id: payload.list?.id || listId,
                item_list_name: payload.list?.name || listName,
              }

          return mapProductToEcommerceItem(
            payload.product,
            payload.variant,
            {
              ...additionalInfo,
              quantity: payload.quantity || 1,
              ...finalListInfo,
            },
            undefined,
          )
        },
      )

      track('begin_checkout', {
        ecommerce: {
          currency: currencyCode,
          value: calculateEcommerceValue(ecommerceItems),
          items: ecommerceItems,
        },
        label: originLocation, // Add later also from basket flyout
        category: 'ecommerce',
        action: 'begin checkout',
      })
    },
    collectBasketItems,
  }
}
export default useCheckoutEvents
